import type React from "react";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import type { ServiceStatus } from "@/hooks/useServices";
import type { DisplayServiceStatus } from "@/lib/service-schedule";
import type { ChipTone } from "@/components/ui/chips";
import type { StatusBadgeVariant } from "@/components/ui/status-badge";
import { TASK_STATUS_CONFIG } from "@/types/status";

// Local type definitions to avoid import issues
type TaskStatus = "todo" | "in_progress" | "done";
type RequestStatus = "open" | "in_progress" | "waiting" | "closed";
type RequestPriority = "low" | "normal" | "high";
type TaskPriority = "low" | "medium" | "high";
type AssetStatus = "available" | "maintenance" | "retired" | "active" | "archived";
type ProjectStatus = "planning" | "active" | "on_hold" | "completed" | "cancelled";

export const serviceStatusMap: Record<DisplayServiceStatus, { label: string; tone: ChipTone }> = {
  draft: { label: "Draft", tone: "neutral" },
  scheduled: { label: "Scheduled", tone: "info" },
  in_progress: { label: "In Progress", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  cancelled: { label: "Cancelled", tone: "error" },
  missed: { label: "Missed", tone: "error" },
};

export const requestStatusMap: Record<RequestStatus, { label: string; variant: StatusBadgeVariant; tone: ChipTone }> = {
  open: { label: "Open", variant: "info", tone: "info" },
  in_progress: { label: "In Progress", variant: "warning", tone: "warning" },
  waiting: { label: "Waiting", variant: "neutral", tone: "neutral" },
  closed: { label: "Closed", variant: "neutral", tone: "neutral" },
  resolved: { label: "Resolved", variant: "success", tone: "success" },
};

export const requestPriorityMap: Record<RequestPriority, { label: string; tone: ChipTone }> = {
  low: { label: "Low", tone: "neutral" },
  normal: { label: "Normal", tone: "info" },
  high: { label: "High", tone: "warning" },
  urgent: { label: "High priority", tone: "error" },
};

export const taskStatusMap: Record<
  keyof typeof TASK_STATUS_CONFIG,
  { label: string; variant: StatusBadgeVariant; tone: ChipTone }
> = {
  todo: {
    label: TASK_STATUS_CONFIG.todo.label,
    variant: TASK_STATUS_CONFIG.todo.variant,
    tone: TASK_STATUS_CONFIG.todo.tone,
  },
  in_progress: {
    label: TASK_STATUS_CONFIG.in_progress.label,
    variant: TASK_STATUS_CONFIG.in_progress.variant,
    tone: TASK_STATUS_CONFIG.in_progress.tone,
  },
  done: {
    label: TASK_STATUS_CONFIG.done.label,
    variant: TASK_STATUS_CONFIG.done.variant,
    tone: TASK_STATUS_CONFIG.done.tone,
  },
};

export const taskPriorityMap: Record<TaskPriority, { label: string; tone: ChipTone }> = {
  low: { label: "Low", tone: "neutral" },
  medium: { label: "Standard", tone: "info" },
  high: { label: "High", tone: "warning" },
};

export const projectStatusMap: Record<ProjectStatus, { label: string; variant: StatusBadgeVariant; tone: ChipTone }> = {
  planning: { label: "Planning", variant: "neutral", tone: "neutral" },
  active: { label: "Active", variant: "info", tone: "info" },
  on_hold: { label: "On Hold", variant: "warning", tone: "warning" },
  completed: { label: "Completed", variant: "success", tone: "success" },
  cancelled: { label: "Cancelled", variant: "neutral", tone: "neutral" },
};

export const assetStatusMap: Record<AssetStatus, { label: string; tone: ChipTone }> = {
  available: { label: "Available", tone: "success" },
  active: { label: "Active", tone: "success" },
  maintenance: { label: "Maintenance", tone: "warning" },
  archived: { label: "Archived", tone: "neutral" },
  retired: { label: "Retired", tone: "neutral" },
};

export type ContributionStatus =
  | "synced"
  | "pending"
  | "error"
  | "failed"
  | "completed"
  | "needs_allocation"
  | "pending_sync"
  | "voided";

export const contributionStatusMap: Record<ContributionStatus, { label: string; variant: StatusBadgeVariant; tone: ChipTone; icon: React.ElementType; tooltip?: string; description?: string }> = {
  synced: { label: "Synced", variant: "success", tone: "success", icon: CheckCircle2, tooltip: "Synced with accounting" },
  pending: { label: "Pending", variant: "warning", tone: "warning", icon: Clock, tooltip: "Processing" },
  error: { label: "Needs Attention", variant: "error", tone: "error", icon: AlertTriangle, tooltip: "Requires review" },
  failed: { label: "Failed", variant: "error", tone: "error", icon: AlertTriangle, tooltip: "Payment failed" },
  completed: { label: "Completed", variant: "success", tone: "success", icon: CheckCircle2 },
  needs_allocation: { label: "Needs Allocation", variant: "warning", tone: "warning", icon: AlertTriangle, tooltip: "Assign to a fund" },
  pending_sync: { label: "Pending Sync", variant: "info", tone: "info", icon: Clock, tooltip: "Waiting to sync" },
  voided: { label: "Voided", variant: "error", tone: "error", icon: AlertTriangle, tooltip: "Contribution voided" },
};

export type AttendanceStatus =
  | "present"
  | "absent"
  | "first_visit"
  | "new"
  | "pickup_verified"
  | "verified";

export const attendanceStatusMap: Record<AttendanceStatus, { label: string; variant: StatusBadgeVariant }> = {
  present: { label: "Present", variant: "success" },
  absent: { label: "Absent", variant: "neutral" },
  first_visit: { label: "First Visit", variant: "info" },
  new: { label: "New", variant: "info" },
  pickup_verified: { label: "Pickup Verified", variant: "success" },
  verified: { label: "Verified", variant: "success" },
};
